import { useRef, useState } from "react";
import { Upload, Eye, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, download, errMsg } from "../lib/api";
import { fdate, run, useLoad } from "../lib/util";
import { Badge, Btn, Card } from "../components/kit";

function Mapping({ t, catalog, onDone }) {
  const set = async (ph, key) => {
    if (await run(() => api.put(`/admin/form-templates/${t.form}/mapping`, { placeholders: { [ph]: key || null } }), "Marcador atualizado")) onDone();
  };
  const entries = Object.entries(t.template.placeholders).map(([id, p]) => [id, p.key, p]);
  if (!entries.length) return <p className="text-xs text-amber-700">Nenhum marcador entre colchetes encontrado neste PDF.</p>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
      {entries.map(([ph, key, p], i) => (
        <div key={ph} className="flex items-center gap-2 text-xs min-w-0" data-testid={`tpl-ph-${t.form}-${i}`}>
          <span className="bg-slate-100 rounded px-2 py-1 truncate max-w-[45%]" title={p.context}><span className="font-mono">[{p.text}]</span> <span className="text-slate-500">{(p.context.match(/(?:esquerda|acima): '([^']+)'/) || [])[1] || ""}</span></span>
          <span>→</span>
          <select data-testid={`tpl-ph-select-${t.form}-${i}`} className={`border rounded px-2 py-1 flex-1 min-w-0 w-full ${key ? "" : "border-amber-400 bg-amber-50"}`} value={key || ""} onChange={(e) => set(ph, e.target.value)}>
            <option value="">— não preencher —</option>
            {Object.entries(catalog).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      ))}
    </div>
  );
}

function Row({ t, catalog, onDone }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const send = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = (await api.post(`/admin/form-templates/${t.form}`, fd, { timeout: 180000 })).data;
      toast.success(`Modelo salvo: ${Object.keys(r.placeholders).length} marcador(es) encontrados`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e), { duration: 9000 });
    }
    setBusy(false);
    ref.current.value = "";
  };
  const del = async () => {
    if (window.confirm(`Remover o modelo de ${t.label}? As escolas deixam de gerar este formulário.`) && (await run(() => api.delete(`/admin/form-templates/${t.form}`), "Modelo removido"))) onDone();
  };
  return (
    <div className="border rounded-lg p-4" data-testid={`tpl-row-${t.form}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-sm text-[#0B192C]">{t.label}</div>
          {t.template ? <div className="text-xs text-slate-500">{t.template.filename} · {t.template.pages} pág. · enviado em {fdate(t.template.uploaded_at?.slice(0, 10))}</div> : <Badge tone="amber">modelo não enviado</Badge>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Btn variant={t.template ? "outline" : undefined} disabled={busy} data-testid={`tpl-upload-${t.form}`} onClick={() => ref.current.click()}>
            <Upload className="w-4 h-4" />{busy ? "Analisando…" : t.template ? "Substituir" : "Enviar modelo em PDF"}
          </Btn>
          <input ref={ref} type="file" accept="application/pdf" className="hidden" data-testid={`tpl-file-${t.form}`} onChange={(e) => send(e.target.files[0])} />
          {t.template && (
            <>
              <Btn variant="outline" data-testid={`tpl-original-${t.form}`} onClick={() => download(`/admin/form-templates/${t.form}/original`, `modelo_${t.form}.pdf`)}><Eye className="w-4 h-4" />Original</Btn>
              <Btn variant="danger" data-testid={`tpl-delete-${t.form}`} onClick={del}><Trash2 className="w-4 h-4" /></Btn>
            </>
          )}
        </div>
      </div>
      {t.template && <Mapping t={t} catalog={catalog} onDone={onDone} />}
    </div>
  );
}

export default function TemplatesTab() {
  const [d, reload] = useLoad(async () => (await api.get("/admin/form-templates")).data, []);
  return (
    <Card className="mt-4">
      <h3 className="font-heading font-bold text-[#0B192C]">Modelos oficiais (iguais para todas as escolas)</h3>
      <p className="text-xs text-slate-500 mb-4">Envie cada formulário em PDF com os campos a preencher entre colchetes, ex.: [Nome do Colégio]. Cada marcador é trocado pelos dados do cadastro da escola e do mês. Confira abaixo a qual dado cada marcador corresponde.</p>
      <div className="space-y-3">{(d?.forms || []).map((t) => <Row key={t.form} t={t} catalog={d.catalog} onDone={reload} />)}</div>
    </Card>
  );
}
